import type { PublicPost } from '@ecowheels/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button, Card, ConfirmButton, PageHeader, QueryState } from '@/components/ui';

function PostCard({ post, onDelete }: { post: PublicPost; onDelete: (id: number) => void }) {
  const [preview, setPreview] = useState(false);

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-medium">{post.title || '(cím nélkül)'}</p>
          <p className="mt-1 truncate text-sm text-white/60">{post.subTitle}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={() => setPreview((v) => !v)} aria-expanded={preview}>
            {preview ? 'Bezárás' : 'Előnézet'}
          </Button>
          <ConfirmButton
            onConfirm={() => onDelete(post.id)}
            confirmText={`Biztosan törli a(z) "${post.title}" bejegyzést? A hozzá tartozó képek is törlődnek. Ez nem vonható vissza.`}
          >
            Törlés
          </ConfirmButton>
        </div>
      </div>

      {preview && (
        <div
          // Same trusted admin-authored HTML the public site renders.
          className="mt-4 border-t border-white/10 pt-4 [&_a]:text-brand [&_img]:max-w-full [&_img]:rounded [&_p]:mt-2 [&_p]:text-white/70"
          dangerouslySetInnerHTML={{ __html: post.html }}
        />
      )}
    </Card>
  );
}

export function PostsPage() {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['posts'],
    queryFn: () => api.get<PublicPost[]>('/admin/post'),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/admin/post/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['posts'] }),
  });

  return (
    <>
      <PageHeader
        title="Bejegyzések"
        action={
          <Link to="/posts/new">
            <Button>Új bejegyzés</Button>
          </Link>
        }
      />

      {remove.error && (
        <p className="mb-4 text-sm text-red-400">A törlés nem sikerült. Kérjük próbálja újra.</p>
      )}

      <QueryState
        isLoading={isLoading}
        error={error}
        isEmpty={(data?.length ?? 0) === 0}
        emptyText="Még nincs bejegyzés."
      >
        <div className="space-y-3">
          {data?.map((post) => (
            <PostCard key={post.id} post={post} onDelete={remove.mutate} />
          ))}
        </div>
      </QueryState>
    </>
  );
}
